import type { CSSProperties, JSX } from "react";
import { useId } from "react";
import { cx } from "../cx";

/**
 * Structural node/edge inputs. The design library stays contract-free:
 * these shapes are a structural subset of the generated Web Studio graph
 * types (`nodeId`/`kind`/`label`, `edgeId`/`sourceNodeId`/`targetNodeId`/
 * `kind`), so generated graphs assign directly without importing generated
 * types into `src/design`.
 */
export interface ProvenanceGraphNodeInput {
  nodeId: string;
  kind: string;
  label: string;
}

export interface ProvenanceGraphEdgeInput {
  edgeId: string;
  sourceNodeId: string;
  targetNodeId: string;
  kind: string;
}

export interface ProvenanceGraphProps {
  nodes?: ReadonlyArray<ProvenanceGraphNodeInput> | undefined;
  edges?: ReadonlyArray<ProvenanceGraphEdgeInput> | undefined;
  /** Accessible name for the graph region landmark. */
  label: string;
  selectedNodeId?: string | null | undefined;
  /** Fires with the node id; nodes render as toggle buttons. */
  onSelectNode?: ((nodeId: string) => void) | undefined;
  /**
   * Overrides the default column assignment (observation, atom, fact,
   * intent, then unknown kinds in first-seen order). Sparse column indexes
   * are compacted, so any deterministic integer assignment works.
   */
  columnOf?: ((node: ProvenanceGraphNodeInput) => number) | undefined;
  /** Message rendered when the graph has no nodes. */
  emptyMessage?: string | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/* Kit-fidelity graph metrics (px allowed inside the design library). */
const NODE_W = 208;
const NODE_H = 52;
const COL_GAP = 64;
const ROW_GAP = 16;
const PAD = 20;
const SAME_COLUMN_BOW = 40;

const DEFAULT_KIND_ORDER = ["observation", "atom", "fact", "intent"] as const;

export interface ProvenanceGraphPlacement {
  x: number;
  y: number;
  column: number;
}

export interface ProvenanceGraphLayout {
  width: number;
  height: number;
  placements: ReadonlyMap<string, ProvenanceGraphPlacement>;
}

function defaultColumnOf(
  nodes: ReadonlyArray<ProvenanceGraphNodeInput>,
): (node: ProvenanceGraphNodeInput) => number {
  const knownKinds: readonly string[] = DEFAULT_KIND_ORDER;
  const extraKinds = new Map<string, number>();
  for (const node of nodes) {
    if (!knownKinds.includes(node.kind) && !extraKinds.has(node.kind)) {
      extraKinds.set(node.kind, knownKinds.length + extraKinds.size);
    }
  }
  return (node) => {
    const known = knownKinds.indexOf(node.kind);
    return known >= 0 ? known : (extraKinds.get(node.kind) ?? knownKinds.length);
  };
}

/**
 * Deterministic layered layout: nodes group into columns (by kind order or
 * the caller's `columnOf`), sparse column indexes compact left-to-right,
 * and each column stacks nodes vertically in input order. No physics, no
 * randomness — the same graph always lays out identically.
 */
export function layoutProvenanceGraph(
  nodes: ReadonlyArray<ProvenanceGraphNodeInput>,
  columnOf?: ((node: ProvenanceGraphNodeInput) => number) | undefined,
): ProvenanceGraphLayout {
  const assign = columnOf ?? defaultColumnOf(nodes);
  const rawColumns = new Map<string, number>();
  const usedColumns = new Set<number>();
  for (const node of nodes) {
    const column = assign(node);
    rawColumns.set(node.nodeId, column);
    usedColumns.add(column);
  }
  const orderedColumns = [...usedColumns].sort((a, b) => a - b);
  const denseIndex = new Map<number, number>(
    orderedColumns.map((column, index) => [column, index]),
  );

  const placements = new Map<string, ProvenanceGraphPlacement>();
  const rowsPerColumn = new Map<number, number>();
  for (const node of nodes) {
    const column = denseIndex.get(rawColumns.get(node.nodeId) ?? 0) ?? 0;
    const rowIndex = rowsPerColumn.get(column) ?? 0;
    rowsPerColumn.set(column, rowIndex + 1);
    placements.set(node.nodeId, {
      column,
      x: PAD + column * (NODE_W + COL_GAP),
      y: PAD + rowIndex * (NODE_H + ROW_GAP),
    });
  }

  const columnCount = orderedColumns.length;
  const maxRows = Math.max(0, ...rowsPerColumn.values());
  return {
    width:
      columnCount === 0 ? PAD * 2 : PAD * 2 + columnCount * NODE_W + (columnCount - 1) * COL_GAP,
    height: maxRows === 0 ? PAD * 2 : PAD * 2 + maxRows * NODE_H + (maxRows - 1) * ROW_GAP,
    placements,
  };
}

/** An edge whose kind names negation renders with the dashed treatment. */
export function isNegationEdgeKind(kind: string): boolean {
  return kind.toLowerCase().includes("negat");
}

function edgePath(source: ProvenanceGraphPlacement, target: ProvenanceGraphPlacement): string {
  const sourceY = source.y + NODE_H / 2;
  const targetY = target.y + NODE_H / 2;
  if (target.column > source.column) {
    const startX = source.x + NODE_W;
    const endX = target.x;
    const reach = Math.max((endX - startX) / 2, 1);
    return `M ${startX} ${sourceY} C ${startX + reach} ${sourceY}, ${endX - reach} ${targetY}, ${endX} ${targetY}`;
  }
  if (target.column < source.column) {
    const startX = source.x;
    const endX = target.x + NODE_W;
    const reach = Math.max((startX - endX) / 2, 1);
    return `M ${startX} ${sourceY} C ${startX - reach} ${sourceY}, ${endX + reach} ${targetY}, ${endX} ${targetY}`;
  }
  const startX = source.x + NODE_W;
  return `M ${startX} ${sourceY} C ${startX + SAME_COLUMN_BOW} ${sourceY}, ${startX + SAME_COLUMN_BOW} ${targetY}, ${startX} ${targetY}`;
}

/**
 * Deterministic layered graph for provenance neighborhoods and ontology
 * rule dependencies. Renders backend-owned nodes and edges verbatim: an
 * SVG underlay draws the edges (dashed when the backend edge kind names
 * negation) while every node is a real toggle button positioned over it,
 * so the graph is keyboard operable and screen-reader addressable. Wide
 * graphs scroll inside the component's own overflow container.
 */
export function ProvenanceGraph({
  nodes = [],
  edges = [],
  label,
  selectedNodeId,
  onSelectNode,
  columnOf,
  emptyMessage,
  className,
  style,
}: ProvenanceGraphProps): JSX.Element {
  const markerId = useId();
  const layout = layoutProvenanceGraph(nodes, columnOf);

  return (
    <section aria-label={label} className={cx("tk-provenance-graph", className)} style={style}>
      {nodes.length === 0 ? (
        <p className="tk-provenance-graph__empty" role="status">
          {emptyMessage ?? "No graph nodes to render."}
        </p>
      ) : (
        <div className="tk-provenance-graph__scroll">
          <div
            className="tk-provenance-graph__canvas"
            style={{ width: layout.width, height: layout.height }}
          >
            <svg
              className="tk-provenance-graph__edges"
              width={layout.width}
              height={layout.height}
              aria-hidden="true"
              focusable="false"
            >
              <defs>
                <marker
                  id={markerId}
                  viewBox="0 0 8 8"
                  refX="7"
                  refY="4"
                  markerWidth="7"
                  markerHeight="7"
                  orient="auto-start-reverse"
                >
                  <path className="tk-provenance-graph__arrow" d="M 0 0 L 8 4 L 0 8 z" />
                </marker>
              </defs>
              {edges.map((edge) => {
                const source = layout.placements.get(edge.sourceNodeId);
                const target = layout.placements.get(edge.targetNodeId);
                if (source === undefined || target === undefined) {
                  return null;
                }
                return (
                  <path
                    key={edge.edgeId}
                    className={cx(
                      "tk-provenance-graph__edge",
                      isNegationEdgeKind(edge.kind) && "tk-provenance-graph__edge--negation",
                    )}
                    d={edgePath(source, target)}
                    markerEnd={`url(#${markerId})`}
                  />
                );
              })}
            </svg>
            {nodes.map((node) => {
              const placement = layout.placements.get(node.nodeId);
              if (placement === undefined) {
                return null;
              }
              const selected = node.nodeId === selectedNodeId;
              return (
                <button
                  key={node.nodeId}
                  type="button"
                  className={cx(
                    "tk-provenance-graph__node",
                    selected && "tk-provenance-graph__node--selected",
                  )}
                  style={{ left: placement.x, top: placement.y, width: NODE_W, height: NODE_H }}
                  aria-pressed={selected}
                  // Explicit name: the visual kind eyebrow is uppercased by
                  // CSS, so the computed name stays "<kind> <label>".
                  aria-label={`${node.kind} ${node.label}`}
                  onClick={onSelectNode === undefined ? undefined : () => onSelectNode(node.nodeId)}
                >
                  <span className="tk-provenance-graph__node-kind">{node.kind}</span>
                  <span className="tk-provenance-graph__node-label">{node.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
